import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import Intercom, { shutdown, update } from '@intercom/messenger-js-sdk';
import { environment } from '@env/environment';
import { of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class IntercomService {
  private initialized = false;
  private activeUserId: string | null = null;
  private loadingUserId: string | null = null;

  constructor(private http: HttpClient) {}

  boot(user: any): void {
    if (!this.canUseIntercom() || !user || !user.data) return;

    const userId = this.getUserId(user.data);
    if (!userId || this.loadingUserId === userId) return;

    if (this.initialized && this.activeUserId === userId) {
      update({});
      return;
    }

    this.loadingUserId = userId;
    this.http.get<any>(`${environment.apiUrlV2}/auth/intercom/messenger-token`).pipe(
      take(1),
      catchError(() => of(null))
    ).subscribe((response) => {
      this.loadingUserId = null;

      if (!response || !response.intercomUserJwt) return;

      Intercom(this.buildSettings(response.intercomUserJwt));
      this.initialized = true;
      this.activeUserId = userId;
    });
  }

  update(): void {
    if (!this.canUseIntercom() || !this.initialized) return;

    update({});
  }

  shutdown(): void {
    if (typeof window === 'undefined') return;

    try {
      shutdown();
    } catch (_) {}

    this.initialized = false;
    this.activeUserId = null;
    this.loadingUserId = null;
  }

  private canUseIntercom(): boolean {
    return !!this.appId && typeof window !== 'undefined' && typeof document !== 'undefined';
  }

  private buildSettings(intercomUserJwt: string): any {
    const settings: any = {
      app_id: this.appId,
      intercom_user_jwt: intercomUserJwt
    };

    if ((environment as any).intercomApiBase) {
      settings.api_base = (environment as any).intercomApiBase;
    }

    return settings;
  }

  private getUserId(data: any): string {
    if (data.account_id && data.login_id) {
      return `account:${data.account_id}:login:${data.login_id}`;
    }

    return data.login_id || data.id || data.email || '';
  }

  private get appId(): string {
    return (environment as any).intercomAppId || '';
  }
}
